import React from 'react'
import { Button, CircularProgress, Dialog, DialogContent, IconButton, MenuItem, Select, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import adlatusLogo from '../../../assets/adlatus_Logo.svg'
import docuthekLogo from '../../../assets/docuthek_logo_croped.jpg'
import assetRegistrationTemplate from '../../../assets/AssetRegistration.xlsx'
import './AssetRegistration.css'
import Pagination from '../../../components/TablePagination'
import LanguageStrings from '../../../i18n/locales'
import { hasSpaceAtStartOrEnd, isValidDeviceData } from '../../../utils/validator'
import { getStoredUsername } from '../../../utils/oidcSession'
import { createAssetRegistration, type CreateAssetRegistrationPostRequest } from '../services/createAssetService'
import { bulkUploadAssets, type BulkUploadResult } from '../services/bulkUploadAssetService'
import { getAssetById, type GetAssetByIdApiResponse } from '../services/getAssetByIdService'
import { getAssetsBySubscription, type GetAssetsBySubscriptionApiResponse } from '../services/getAssetsBySubscriptionService'
import { updateAsset, type UpdateAssetPutRequest } from '../services/updateAssetService'
import { APIConfiguration } from '../../../utils/apiConfiguration'
import CustomToast from '../../asset-managament/components/ToastComponent'

const MAX_SERIAL_NUMBER_LENGTH = 100
const ALLOWED_BULK_FILE_EXTENSIONS = ['.xlsx', '.xls']

const getCurrentDateValue = (): string => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = String(currentDate.getMonth() + 1).padStart(2, '0')
  const day = String(currentDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatLocalDateValue = (dateValue: Date): string => {
  const year = dateValue.getFullYear()
  const month = String(dateValue.getMonth() + 1).padStart(2, '0')
  const day = String(dateValue.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatApiDateToLocalDate = (apiDateValue: string): string => {
  const resolvedDate = new Date(apiDateValue)

  if (Number.isNaN(resolvedDate.getTime())) {
    return ''
  }

  return formatLocalDateValue(resolvedDate)
}

const PRODUCT_FAMILY_OPTIONS = [
  { value: 'a5bdb843-7be6-4ff4-8734-d670b4bc5df8', label: 'Burner & Heat Exchangers' },
  { value: 'fb312ad6-49e1-4348-8462-e1ec887ea339', label: 'Burner Management Systems' },
  { value: '0308fe32-2e96-4293-9233-b376247326c1', label: 'Fuel & Air Delivery' },
  { value: '3b3e3ccd-c853-4751-b837-b764beaa9a73', label: 'Complete Combustion Solutions' }
]

const ASSET_NAME_OPTIONS = ['7800 Series', 'SLATE', 'BCU 4xx', 'BCU 5xx']
const FIXED_PRODUCT_FAMILY_LABEL = 'Burner Management Systems'
const FIXED_PRODUCT_FAMILY_ID = PRODUCT_FAMILY_OPTIONS.find((option) => option.label === FIXED_PRODUCT_FAMILY_LABEL)?.value ?? 'fb312ad6-49e1-4348-8462-e1ec887ea339'

interface ParsedQrRegistrationPayload {
  serialNumber: string
  productFamily: string
  assetName: string
  assetDescription: string
}

interface RegisteredAssetRow {
  id: string
  productAssetName: string
  accountName: string
  siteLocationName: string
  productFamily: string
  recordedDate: string
  serialNumber: string
}

interface AssetDetailsFormState {
  name: string
  assetTypeId: string
  timezone: string
  applicationType: string
  description: string
  installDate: string
}

type AssetDetailsMode = 'view' | 'update'

const QR_SCANNER_CONTAINER_ID = 'asset-registration-qr-reader'
const QR_SCANNER_BOX_SIZE = 300

const DEFAULT_ASSET_DETAILS_FORM_STATE: AssetDetailsFormState = {
  name: '',
  assetTypeId: '',
  timezone: '',
  applicationType: '',
  description: '',
  installDate: getCurrentDateValue()
}

const ASSET_DOCUMENTATION_URL = 'https://docuthek.kromschroeder.com/index.php?lang=en&selclass=&sellang=&folder=2'
const ADLATUS_PORTAL_URL = 'https://www.kromschroeder.de/marketing/adlatus/00_adlatus/adlatus.html'

const normalizeQrKey = (rawKey: string): string => {
  return rawKey.toLowerCase().replace(/[^a-z0-9]/g, '')
}

const toQrMap = (decodedText: string): Map<string, string> => {
  const qrMap = new Map<string, string>()
  const trimmedText = decodedText.trim()

  if (trimmedText === '') {
    return qrMap
  }

  const candidateEntries = trimmedText.split(';')

  candidateEntries.forEach((entry) => {
    const trimmedEntry = entry.trim()

    if (trimmedEntry === '') {
      return
    }

    const separatorIndex = trimmedEntry.indexOf('=')

    if (separatorIndex <= 0) {
      return
    }

    const key = normalizeQrKey(trimmedEntry.slice(0, separatorIndex))
    const value = trimmedEntry.slice(separatorIndex + 1).trim()

    if (key !== '' && value !== '') {
      qrMap.set(key, value)
    }
  })

  return qrMap
}

const resolveQrValue = (qrMap: Map<string, string>, keys: string[]): string => {
  const matchedKey = keys.find((key) => qrMap.has(normalizeQrKey(key)))

  if (matchedKey === undefined) {
    return ''
  }

  return qrMap.get(normalizeQrKey(matchedKey)) ?? ''
}

const resolveProductFamilyFromQrValue = (rawValue: string): string => {
  const trimmedValue = rawValue.trim()

  if (trimmedValue === '') {
    return ''
  }

  const normalizedValue = normalizeQrKey(trimmedValue)

  const matchedById = PRODUCT_FAMILY_OPTIONS.find((option) => option.value.toLowerCase() === trimmedValue.toLowerCase())

  if (matchedById !== undefined) {
    return matchedById.value
  }

  const matchedByLabel = PRODUCT_FAMILY_OPTIONS.find((option) => option.label.toLowerCase() === trimmedValue.toLowerCase())

  if (matchedByLabel !== undefined) {
    return matchedByLabel.value
  }

  const matchedByNormalizedLabel = PRODUCT_FAMILY_OPTIONS.find((option) => normalizeQrKey(option.label) === normalizedValue)

  if (matchedByNormalizedLabel !== undefined) {
    return matchedByNormalizedLabel.value
  }

  return ''
}

const parseQrRegistrationPayload = (decodedText: string): ParsedQrRegistrationPayload => {
  const qrMap = toQrMap(decodedText)

  if (qrMap.size === 0) {
    return {
      serialNumber: '',
      productFamily: '',
      assetName: '',
      assetDescription: ''
    }
  }

  const serialNumber = resolveQrValue(qrMap, ['serialNumber', 'serial_number', 'serial', 'sn'])
  const productFamilyRawValue = resolveQrValue(qrMap, ['productFamily', 'product_family', 'assetTypeId', 'asset_type_id', 'productFamilyId'])
  const assetName = resolveQrValue(qrMap, ['assetName', 'asset_name', 'name'])
  const assetDescription = resolveQrValue(qrMap, ['assetDescription', 'asset_description', 'description'])

  return {
    serialNumber,
    productFamily: resolveProductFamilyFromQrValue(productFamilyRawValue),
    assetName,
    assetDescription
  }
}

const resolveAssetNameFromInput = (rawValue: string): string => {
  const trimmedValue = rawValue.trim()

  if (trimmedValue === '') {
    return ''
  }

  const directMatch = ASSET_NAME_OPTIONS.find((option) => option.toLowerCase() === trimmedValue.toLowerCase())

  if (directMatch !== undefined) {
    return directMatch
  }

  const normalizedInput = normalizeQrKey(trimmedValue)
  const normalizedMatch = ASSET_NAME_OPTIONS.find((option) => normalizeQrKey(option) === normalizedInput)

  return normalizedMatch ?? ''
}

const getProductFamilyLabelById = (productFamilyId: string): string => {
  const resolvedLabel = PRODUCT_FAMILY_OPTIONS.find((option) => option.value === productFamilyId)?.label

  if (resolvedLabel !== undefined) {
    return resolvedLabel
  }

  return productFamilyId
}

const AssetRegistration = (): JSX.Element => {
  const deviceInformationStrings = LanguageStrings().DeviceManagementCollectionStrings.DeviceInformationComponent
  const storedUserName = getStoredUsername().trim()
  const [serialNumber, setSerialNumber] = React.useState('')
  const [serialNumberTouched, setSerialNumberTouched] = React.useState(false)
  const [selectedProductFamily, setSelectedProductFamily] = React.useState(FIXED_PRODUCT_FAMILY_ID)
  const [isReviewStep, setIsReviewStep] = React.useState(false)
  const [isRegisteredAssetsView, setIsRegisteredAssetsView] = React.useState(false)
  const [isInitialViewLoading, setIsInitialViewLoading] = React.useState(true)
  const [isQrScannerOpen, setIsQrScannerOpen] = React.useState(false)
  const [isQrScannerDialogReady, setIsQrScannerDialogReady] = React.useState(false)
  const [isQrScannerLoading, setIsQrScannerLoading] = React.useState(false)
  const [qrScannerError, setQrScannerError] = React.useState<string | null>(null)
  const [assetLocation, setAssetLocation] = React.useState('')
  const [accountName, setAccountName] = React.useState('')
  const [assetName, setAssetName] = React.useState('')
  const [useScannedAssetNameFreeText, setUseScannedAssetNameFreeText] = React.useState(false)
  const [assetDescription, setAssetDescription] = React.useState('')
  const [recordedDate, setRecordedDate] = React.useState(getCurrentDateValue())
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isBulkRegisterModalOpen, setIsBulkRegisterModalOpen] = React.useState(false)
  const [bulkRegisterFile, setBulkRegisterFile] = React.useState<File | null>(null)
  const [bulkRegisterError, setBulkRegisterError] = React.useState<string | null>(null)
  const [bulkRegisterResult, setBulkRegisterResult] = React.useState<BulkUploadResult | null>(null)
  const [isBulkRegisterSubmitting, setIsBulkRegisterSubmitting] = React.useState(false)
  const [registeredAssetRows, setRegisteredAssetRows] = React.useState<RegisteredAssetRow[]>([])
  const [isAssetsLoading, setIsAssetsLoading] = React.useState(false)
  const [registeredAssetsPage, setRegisteredAssetsPage] = React.useState(1)
  const [registeredAssetsCount, setRegisteredAssetsCount] = React.useState(0)
  const registeredAssetsRowsPerPage = 8
  const registeredAssetsSortBy = 'installDate'
  const [registeredAssetsSortDirection, setRegisteredAssetsSortDirection] = React.useState<'asc' | 'desc'>('desc')
  const [registeredAssetsSearchInput, setRegisteredAssetsSearchInput] = React.useState('')
  const [registeredAssetsSearchTerm, setRegisteredAssetsSearchTerm] = React.useState('')
  const [isAssetDetailsView, setIsAssetDetailsView] = React.useState(false)
  const [assetDetailsMode, setAssetDetailsMode] = React.useState<AssetDetailsMode>('view')
  const [isAssetDetailsLoading, setIsAssetDetailsLoading] = React.useState(false)
  const [isUpdatingAssetDetails, setIsUpdatingAssetDetails] = React.useState(false)
  const [viewAssetError, setViewAssetError] = React.useState<string | null>(null)
  const [updateAssetError, setUpdateAssetError] = React.useState<string | null>(null)
  const [selectedAssetDetails, setSelectedAssetDetails] = React.useState<GetAssetByIdApiResponse | null>(null)
  const [assetDetailsForm, setAssetDetailsForm] = React.useState<AssetDetailsFormState>(DEFAULT_ASSET_DETAILS_FORM_STATE)
  const qrScannerRef = React.useRef<Html5Qrcode | null>(null)
  const isQrScannerStoppingRef = React.useRef(false)

  const trimmedSerialNumber = serialNumber.trim()
  const isSerialNumberEmpty = trimmedSerialNumber === ''
  const isSerialNumberTooLong = serialNumber.length > MAX_SERIAL_NUMBER_LENGTH
  const isSerialNumberFormattedWrong = !isSerialNumberEmpty && !isValidDeviceData(serialNumber)
  const hasSerialNumberEdgeSpaces = hasSpaceAtStartOrEnd(serialNumber)
  const isSerialNumberValid = !isSerialNumberEmpty && !isSerialNumberTooLong && !hasSerialNumberEdgeSpaces && !isSerialNumberFormattedWrong
  const hasAssetName = assetName.trim() !== ''
  const hasLocation = assetLocation.trim() !== ''
  const hasAccountName = accountName.trim() !== ''
  const hasDescription = assetDescription.trim() !== ''
  const hasRecordedDate = recordedDate !== ''
  const isSubmitDisabled = isSubmitting || !isSerialNumberValid || !hasAssetName || !hasLocation || !hasAccountName || !hasDescription || !hasRecordedDate
  const isBulkRegisterDisabled = isBulkRegisterSubmitting || bulkRegisterFile === null
  const canUseQrScanner = typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function'
  const isUpdateDetailsDisabled =
    isUpdatingAssetDetails ||
    assetDetailsMode !== 'update' ||
    selectedAssetDetails === null ||
    (assetDetailsForm.assetTypeId === FIXED_PRODUCT_FAMILY_ID ? !ASSET_NAME_OPTIONS.includes(assetDetailsForm.name) : assetDetailsForm.name.trim() === '') ||
    assetDetailsForm.assetTypeId.trim() === '' ||
    assetDetailsForm.timezone.trim() === '' ||
    assetDetailsForm.applicationType.trim() === '' ||
    assetDetailsForm.description.trim() === '' ||
    assetDetailsForm.installDate === ''
  const isSelectedProductFamilyDifferent = selectedProductFamily !== '' && selectedProductFamily !== FIXED_PRODUCT_FAMILY_ID
  const selectedProductFamilyLabel = getProductFamilyLabelById(selectedProductFamily)
  const isAssetDetailsProductFamilyDifferent = assetDetailsForm.assetTypeId !== '' && assetDetailsForm.assetTypeId !== FIXED_PRODUCT_FAMILY_ID
  const assetDetailsProductFamilyLabel = getProductFamilyLabelById(assetDetailsForm.assetTypeId)

  const getSerialNumberErrorMessage = (): string => {
    if (isSerialNumberEmpty) {
      return deviceInformationStrings.SerialNumberError
    }
    if (isSerialNumberTooLong) {
      return deviceInformationStrings.SerialNumberLengthError
    }
    if (hasSerialNumberEdgeSpaces) {
      return deviceInformationStrings.NotAllowedError
    }
    return deviceInformationStrings.SerialNumberErrorMsg
  }

  const showSuccessToast = (message: string): void => {
    CustomToast({
      type: 'success',
      heading: 'Success',
      message
    })
  }

  const showErrorToast = (message: string): void => {
    CustomToast({
      type: 'error',
      heading: 'Failed',
      message
    })
  }

  const handleContinueClick = (): void => {
    setIsRegisteredAssetsView(false)
    setIsReviewStep(true)
  }

  const runScannerOperationSafely = async (operation: () => void | Promise<void>): Promise<void> => {
    try {
      await Promise.resolve(operation())
    } catch {
      // Ignore scanner teardown errors so popup close can always proceed.
    }
  }

  const stopQrScanner = async (): Promise<void> => {
    const scanner = qrScannerRef.current

    if (scanner === null || isQrScannerStoppingRef.current) {
      return
    }

    isQrScannerStoppingRef.current = true

    try {
      const scannerState = scanner.getState()

      if (scannerState !== Html5QrcodeScannerState.NOT_STARTED) {
        await runScannerOperationSafely(async () => { await scanner.stop() })
      }

      await runScannerOperationSafely(async () => { await scanner.clear() })
    } catch {
      await runScannerOperationSafely(async () => { await scanner.clear() })
    } finally {
      if (qrScannerRef.current === scanner) {
        qrScannerRef.current = null
      }
      isQrScannerStoppingRef.current = false
    }
  }

  const handleOpenQrScanner = (): void => {
    setQrScannerError(null)
    setIsQrScannerDialogReady(false)
    setIsQrScannerOpen(true)
  }

  const handleCloseQrScanner = (): void => {
    setIsQrScannerOpen(false)
    setIsQrScannerDialogReady(false)
    setIsQrScannerLoading(false)
    setQrScannerError(null)
    void stopQrScanner()
  }

  const handleChangeAsset = (): void => {
    setIsReviewStep(false)
    setIsRegisteredAssetsView(false)
    setSelectedProductFamily(FIXED_PRODUCT_FAMILY_ID)
    setSerialNumberTouched(false)
  }

  const handleCancelReview = (): void => {
    setIsReviewStep(false)
  }

  const handleRegisterAssetClick = (): void => {
    resetRegistrationForm()
    handleCloseAssetDetailsView()
    setIsRegisteredAssetsView(false)
    setIsReviewStep(false)
  }

  const handleOpenBulkRegisterModal = (): void => {
    setBulkRegisterFile(null)
    setBulkRegisterError(null)
    setBulkRegisterResult(null)
    setIsBulkRegisterModalOpen(true)
  }

  const handleCloseBulkRegisterModal = (): void => {
    if (isBulkRegisterSubmitting) {
      return
    }

    setIsBulkRegisterModalOpen(false)
    setBulkRegisterFile(null)
    setBulkRegisterError(null)
    setBulkRegisterResult(null)
  }

  const validateBulkFileSelection = (file: File): string | null => {
    const lowerCaseName = file.name.toLowerCase()
    const hasValidExtension = ALLOWED_BULK_FILE_EXTENSIONS.some((extension) => lowerCaseName.endsWith(extension))

    if (!hasValidExtension) {
      return 'Only .xlsx or .xls files are supported.'
    }

    if (file.size === 0) {
      return 'Selected file is empty. Please upload a valid file.'
    }

    return null
  }

  const handleBulkRegisterFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0]

    if (selectedFile === undefined) {
      setBulkRegisterFile(null)
      return
    }

    const validationError = validateBulkFileSelection(selectedFile)

    if (validationError !== null) {
      setBulkRegisterError(validationError)
      setBulkRegisterFile(null)
      setBulkRegisterResult(null)
      return
    }

    setBulkRegisterError(null)
    setBulkRegisterResult(null)
    setBulkRegisterFile(selectedFile)
  }

  const handleBulkRegisterSubmit = (): void => {
    if (bulkRegisterFile === null || isBulkRegisterSubmitting) {
      return
    }

    setIsBulkRegisterSubmitting(true)
    setBulkRegisterError(null)

    void bulkUploadAssets(
      bulkRegisterFile,
      (responseData, httpStatus) => {
        if (httpStatus !== 207) {
          setIsBulkRegisterSubmitting(false)
          setBulkRegisterError('Unexpected response status from bulk upload API.')
          showErrorToast('Failed to process bulk upload. Please try again.')
          return
        }

        if (responseData.success_count === 0) {
          setIsBulkRegisterSubmitting(false)
          setBulkRegisterResult(responseData)
          setBulkRegisterError('No assets were registered. Please review failed rows and retry.')
          showErrorToast('Bulk upload failed for all rows. Please review failure reasons.')
          return
        }

        if (responseData.failure_count > 0) {
          setIsBulkRegisterSubmitting(false)
          setBulkRegisterResult(responseData)
          setBulkRegisterError('Some rows failed. Please review failed rows below.')
          showErrorToast(`Bulk upload partially completed. Success: ${responseData.success_count}, Failed: ${responseData.failure_count}`)
          fetchRegisteredAssets()
          return
        }

        setIsBulkRegisterSubmitting(false)
        setIsBulkRegisterModalOpen(false)
        setBulkRegisterFile(null)
        setBulkRegisterError(null)
        setBulkRegisterResult(null)
        setIsReviewStep(false)
        setIsAssetDetailsView(false)
        setIsRegisteredAssetsView(true)
        setRegisteredAssetsPage(1)
        setRegisteredAssetsSortDirection('desc')
        setRegisteredAssetsSearchInput('')
        setRegisteredAssetsSearchTerm('')
        showSuccessToast(`Bulk upload processed. Success: ${responseData.success_count}, Failed: ${responseData.failure_count}`)
        fetchRegisteredAssets()
      },
      (errorMessage) => {
        setIsBulkRegisterSubmitting(false)
        const resolvedErrorMessage = errorMessage !== '' ? errorMessage : 'Bulk upload failed. Please try again.'
        setBulkRegisterError(resolvedErrorMessage)
        showErrorToast(resolvedErrorMessage)
      }
    )
  }

  const handleRegisteredAssetsClick = (): void => {
    setIsRegisteredAssetsView(true)
    setIsAssetDetailsView(false)
    setIsReviewStep(false)
    setRegisteredAssetsPage(1)
    setRegisteredAssetsSortDirection('desc')
    setRegisteredAssetsSearchInput('')
    setRegisteredAssetsSearchTerm('')
  }

  const handleRegisteredAssetsSearchApply = (): void => {
    const trimmedSearch = registeredAssetsSearchInput.trim()

    setRegisteredAssetsPage(1)
    setRegisteredAssetsSearchTerm(trimmedSearch)
  }

  const handleRegisteredAssetsSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleRegisteredAssetsSearchApply()
    }
  }

  const handleRecordedDateSortToggle = (): void => {
    setRegisteredAssetsPage(1)
    setRegisteredAssetsSortDirection((currentDirection) => currentDirection === 'desc' ? 'asc' : 'desc')
  }

  const mapAssetRows = (assets: GetAssetsBySubscriptionApiResponse[]): RegisteredAssetRow[] => {
    return assets.map((asset) => ({
      id: asset.id,
      productAssetName: asset.name,
      accountName: asset.application_type,
      siteLocationName: asset.timezone,
      productFamily: asset.asset_type_name,
      recordedDate: formatApiDateToLocalDate(asset.install_date),
      serialNumber: asset.serial_number
    }))
  }

  const fetchRegisteredAssets = (): void => {
    setIsAssetsLoading(true)

    void getAssetsBySubscription(
      registeredAssetsPage,
      registeredAssetsRowsPerPage,
      registeredAssetsSortBy,
      registeredAssetsSortDirection,
      registeredAssetsSearchTerm,
      (responseData, httpStatus) => {
        const isAcceptedStatus = httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS

        if (!isAcceptedStatus) {
          setIsAssetsLoading(false)
          setRegisteredAssetRows([])
          showErrorToast('Failed to fetch registered assets. Please try again.')
          return
        }

        setRegisteredAssetsCount(responseData.total_count)
        setRegisteredAssetRows(mapAssetRows(responseData.assets))
        setIsAssetsLoading(false)
      },
      (errorMessage) => {
        setIsAssetsLoading(false)
        setRegisteredAssetRows([])
        showErrorToast(errorMessage !== '' ? errorMessage : 'Failed to fetch registered assets. Please try again.')
      }
    )
  }

  React.useEffect(() => {
    setIsInitialViewLoading(true)

    void getAssetsBySubscription(
      1,
      registeredAssetsRowsPerPage,
      registeredAssetsSortBy,
      'desc',
      undefined,
      (responseData, httpStatus) => {
        const isAcceptedStatus = httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS

        if (!isAcceptedStatus) {
          setIsRegisteredAssetsView(false)
          setIsInitialViewLoading(false)
          return
        }

        const hasAssets = responseData.total_count > 0
        setIsRegisteredAssetsView(hasAssets)
        if (hasAssets) {
          setRegisteredAssetsPage(1)
          setRegisteredAssetsSortDirection('desc')
        }
        setIsInitialViewLoading(false)
      },
      () => {
        setIsRegisteredAssetsView(false)
        setIsInitialViewLoading(false)
      }
    )
  }, [])

  React.useEffect(() => {
    if (isRegisteredAssetsView) {
      fetchRegisteredAssets()
    }
  }, [isRegisteredAssetsView, registeredAssetsPage, registeredAssetsSortDirection, registeredAssetsSearchTerm])

  React.useEffect(() => {
    if (!isQrScannerOpen || !isQrScannerDialogReady) {
      return
    }
    let isCancelled = false

    const startScanner = async (): Promise<void> => {
      setIsQrScannerLoading(true)
      setQrScannerError(null)

      try {
        if (isCancelled) {
          return
        }

        if (qrScannerRef.current !== null) {
          await stopQrScanner()
        }

        const scanner = new Html5Qrcode(QR_SCANNER_CONTAINER_ID)
        qrScannerRef.current = scanner

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 12,
            qrbox: { width: QR_SCANNER_BOX_SIZE, height: QR_SCANNER_BOX_SIZE },
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
          },
          (decodedText: string) => {
            const parsedPayload = parseQrRegistrationPayload(decodedText)
            const scannedSerialNumber = parsedPayload.serialNumber.trim()

            if (scannedSerialNumber === '') {
              showErrorToast('Unsupported QR format.')
              return
            }

            setSerialNumber(scannedSerialNumber)
            setSerialNumberTouched(true)

            if (parsedPayload.productFamily !== '') {
              setSelectedProductFamily(parsedPayload.productFamily)
            }

            if (parsedPayload.assetName !== '') {
              const resolvedAssetName = resolveAssetNameFromInput(parsedPayload.assetName)
              if (resolvedAssetName !== '') {
                setAssetName(resolvedAssetName)
                setUseScannedAssetNameFreeText(false)
              } else {
                setAssetName(parsedPayload.assetName.trim())
                setUseScannedAssetNameFreeText(true)
              }
            } else {
              setUseScannedAssetNameFreeText(false)
            }

            if (parsedPayload.assetDescription !== '') {
              setAssetDescription(parsedPayload.assetDescription)
            }

            const hasPrefilledFields =
              parsedPayload.assetName !== '' ||
              parsedPayload.assetDescription !== ''

            showSuccessToast(hasPrefilledFields ? 'QR scanned and form fields prefilled.' : 'Serial number scanned successfully.')
            handleCloseQrScanner()
          }
        )

        if (!isCancelled) {
          setIsQrScannerLoading(false)
        }
      } catch (error) {
        if (!isCancelled) {
          setQrScannerError('Unable to access camera. Please check browser camera permission and HTTPS/localhost access, then try again.')
          setIsQrScannerLoading(false)
        }
      }
    }

    void startScanner()

    return () => {
      isCancelled = true
      void stopQrScanner()
    }
  }, [isQrScannerOpen, isQrScannerDialogReady])

  const handleCloseAssetDetailsView = (): void => {
    setIsAssetDetailsView(false)
    setAssetDetailsMode('view')
    setIsAssetDetailsLoading(false)
    setIsUpdatingAssetDetails(false)
    setViewAssetError(null)
    setUpdateAssetError(null)
    setSelectedAssetDetails(null)
    setAssetDetailsForm(DEFAULT_ASSET_DETAILS_FORM_STATE)
  }

  const mapAssetDetailsToForm = (assetDetails: GetAssetByIdApiResponse): AssetDetailsFormState => {
    return {
      name: assetDetails.name,
      assetTypeId: assetDetails.asset_type_id,
      timezone: assetDetails.timezone,
      applicationType: assetDetails.application_type,
      description: assetDetails.description,
      installDate: formatApiDateToLocalDate(assetDetails.install_date)
    }
  }

  const handleOpenAssetDetailsView = (assetId: string, mode: AssetDetailsMode = 'view'): void => {
    setIsAssetDetailsView(true)
    setAssetDetailsMode(mode)
    setIsRegisteredAssetsView(false)
    setIsAssetDetailsLoading(true)
    setViewAssetError(null)
    setUpdateAssetError(null)
    setSelectedAssetDetails(null)

    void getAssetById(
      assetId,
      (assetDetails, httpStatus) => {
        const isAcceptedStatus = httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS

        if (!isAcceptedStatus) {
          setIsAssetDetailsLoading(false)
          setViewAssetError('Failed to fetch asset details. Please try again.')
          showErrorToast('Failed to fetch asset details. Please try again.')
          return
        }

        setSelectedAssetDetails(assetDetails)
        const mappedForm = mapAssetDetailsToForm(assetDetails)

        if (mode === 'update') {
          const normalizedAssetName = resolveAssetNameFromInput(mappedForm.name)

          // Keep existing API value when it does not match the constrained dropdown values.
          mappedForm.name = normalizedAssetName !== '' ? normalizedAssetName : mappedForm.name
        }

        setAssetDetailsForm(mappedForm)
        setIsAssetDetailsLoading(false)
      },
      (errorMessage) => {
        setIsAssetDetailsLoading(false)
        const resolvedErrorMessage = errorMessage !== '' ? errorMessage : 'Failed to fetch asset details. Please try again.'
        setViewAssetError(resolvedErrorMessage)
        showErrorToast(resolvedErrorMessage)
      }
    )
  }

  const handleOpenAssetDocumentation = (): void => {
    window.open(ASSET_DOCUMENTATION_URL, '_blank', 'noopener,noreferrer')
  }

  const handleOpenAdlatusPortal = (): void => {
    window.open(ADLATUS_PORTAL_URL, '_blank', 'noopener,noreferrer')
  }

  const buildUpdateAssetRequest = (): UpdateAssetPutRequest | null => {
    if (selectedAssetDetails === null) {
      return null
    }

    return {
      name: assetDetailsForm.name.trim(),
      serial_number: selectedAssetDetails.serial_number,
      install_date: new Date().toISOString(),
      asset_type_id: assetDetailsForm.assetTypeId,
      timezone: assetDetailsForm.timezone.trim(),
      application_type: assetDetailsForm.applicationType.trim(),
      description: assetDetailsForm.description.trim()
    }
  }

  const handleUpdateAssetDetails = (): void => {
    if (isUpdateDetailsDisabled || selectedAssetDetails === null) {
      return
    }

    const requestBody = buildUpdateAssetRequest()

    if (requestBody === null) {
      return
    }

    setIsUpdatingAssetDetails(true)
    setUpdateAssetError(null)

    void updateAsset(
      selectedAssetDetails.id,
      requestBody,
      (updatedAsset, httpStatus) => {
        const isAcceptedStatus = httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS

        if (!isAcceptedStatus) {
          setIsUpdatingAssetDetails(false)
          setUpdateAssetError('Failed to update asset details. Please try again.')
          showErrorToast('Failed to update asset details. Please try again.')
          return
        }

        setSelectedAssetDetails(updatedAsset)
        setAssetDetailsForm(mapAssetDetailsToForm(updatedAsset))
        setUpdateAssetError(null)
        setIsUpdatingAssetDetails(false)
        setIsAssetDetailsView(false)
        setIsRegisteredAssetsView(true)
        showSuccessToast('Asset details updated successfully.')
        fetchRegisteredAssets()
      },
      (errorMessage) => {
        setIsUpdatingAssetDetails(false)
        const resolvedErrorMessage = errorMessage !== '' ? errorMessage : 'Failed to update asset details. Please try again.'
        setUpdateAssetError(resolvedErrorMessage)
        showErrorToast(resolvedErrorMessage)
      }
    )
  }

  const resetRegistrationForm = (): void => {
    setSerialNumber('')
    setSerialNumberTouched(false)
    setSelectedProductFamily(FIXED_PRODUCT_FAMILY_ID)
    setAssetLocation('')
    setAccountName('')
    setAssetName('')
    setUseScannedAssetNameFreeText(false)
    setAssetDescription('')
    setRecordedDate(getCurrentDateValue())
  }

  const buildCreateAssetRequest = (): CreateAssetRegistrationPostRequest => {
    const resolvedAssetName = assetName.trim() !== '' ? assetName.trim() : `Asset-${trimmedSerialNumber}`
    const resolvedDescription = assetDescription.trim()
    const resolvedRecordedDate = new Date().toISOString()

    return {
      name: resolvedAssetName,
      serial_number: trimmedSerialNumber,
      install_date: resolvedRecordedDate,
      asset_type_id: selectedProductFamily,
      timezone: assetLocation.trim(),
      application_type: accountName.trim(),
      description: resolvedDescription
    }
  }

  const handleSubmitAsset = (): void => {
    if (isSubmitDisabled) {
      return
    }

    const requestBody = buildCreateAssetRequest()
    setIsSubmitting(true)

    void createAssetRegistration(
      requestBody,
      (successResponse, httpStatus) => {
        const isAcceptedStatus =
          httpStatus === APIConfiguration.CREATED_HTTP_STATUS ||
          httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS

        if (!isAcceptedStatus) {
          setIsSubmitting(false)
          showErrorToast('Failed to register asset. Please try again.')
          return
        }

        setIsSubmitting(false)
        showSuccessToast('Asset has been registered successfully.')
        setIsReviewStep(false)
        setIsRegisteredAssetsView(true)
        setIsAssetDetailsView(false)
        resetRegistrationForm()
      },
      (errorMessage) => {
        setIsSubmitting(false)
        showErrorToast(errorMessage !== '' ? errorMessage : 'Failed to register asset. Please try again.')
      }
    )
  }

  if (isInitialViewLoading) {
    return (
      <div className='asset-registration-page'>
        <header className='asset-registration-brand-header'>
          <div className='asset-registration-brand'>
            <span className='asset-registration-brand-mark'>Honeywell Technologies</span>
            <span className='asset-registration-brand-divider' />
            <span className='asset-registration-brand-subtitle'>HONEYWELL THERMAL SOLUTIONS</span>
          </div>
        </header>
        <div className='asset-registration-initial-loader'>
          <CircularProgress size={40} />
        </div>
      </div>
    )
  }

  return (
    <div className={`asset-registration-page ${(isReviewStep || isAssetDetailsView) ? 'asset-registration-page--review' : ''} ${isRegisteredAssetsView ? 'asset-registration-page--table' : ''} ${!isRegisteredAssetsView ? 'asset-registration-page--form' : ''}`}>
      <header className='asset-registration-brand-header'>
        <div className='asset-registration-brand'>
          <span className='asset-registration-brand-mark'>Honeywell Technologies</span>
          <span className='asset-registration-brand-divider' />
          <span className='asset-registration-brand-subtitle'>HONEYWELL THERMAL SOLUTIONS</span>
        </div>
        {isRegisteredAssetsView && (
          <span className='asset-registration-brand-user'>
            <span className='asset-registration-brand-user-name'>{`Hi, ${storedUserName !== '' ? storedUserName : 'there'}`}</span>
            <span className='asset-registration-brand-user-welcome'>Welcome to Asset Registration</span>
          </span>
        )}
      </header>

      <div className='asset-registration-shell'>
        <div className='asset-registration-sticky-nav'>
          <div className='asset-registration-header-row'>
            <h1>
              {isAssetDetailsView
                ? 'Asset Details'
                : isRegisteredAssetsView
                  ? 'Registered Assets'
                  : 'Asset Registration'}
            </h1>
            <div className='asset-registration-header-meta'>
              {isAssetDetailsView && (
                <button
                  type='button'
                  className='asset-registration-header-inline-link'
                  onClick={handleOpenAssetDocumentation}
                  aria-label='Open Documentation'
                  title='Documentation'
                >
                  Documentation
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={`asset-registration-content ${isRegisteredAssetsView ? 'asset-registration-content--table' : ''} ${isAssetDetailsView ? 'asset-registration-content--details' : ''}`}>
          <section className={`asset-registration-form-panel ${!isRegisteredAssetsView ? 'asset-registration-form-panel--flow' : ''}`}>
            {isAssetDetailsView
              ? (
                  <>
                    <div className='asset-registration-details-header asset-registration-details-header--actions-only' />

                    {isAssetDetailsLoading && (
                      <div className='asset-registration-table-placeholder'>
                        <CircularProgress size={24} />
                      </div>
                    )}

                    {!isAssetDetailsLoading && viewAssetError !== null && (
                      <div className='asset-registration-inline-message'>Unable to load asset details.</div>
                    )}

                    {!isAssetDetailsLoading && viewAssetError === null && selectedAssetDetails !== null && (
                      <>
                        {updateAssetError !== null && (
                          <div className='asset-registration-inline-message'>{updateAssetError}</div>
                        )}

                        <div className='asset-registration-review-grid'>
                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-serial-number'>Serial Number</label>
                            <TextField id='asset-details-serial-number' size='small' fullWidth value={selectedAssetDetails.serial_number} disabled />
                          </div>

                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-name'>Product / Asset Name</label>
                            {assetDetailsForm.assetTypeId === FIXED_PRODUCT_FAMILY_ID
                              ? (
                                <Select
                                  id='asset-details-name'
                                  size='small'
                                  displayEmpty
                                  fullWidth
                                  value={assetDetailsForm.name}
                                  disabled={assetDetailsMode === 'view'}
                                  onChange={(event) => {
                                    setAssetDetailsForm((previousState) => ({
                                      ...previousState,
                                      name: event.target.value,
                                      assetTypeId: FIXED_PRODUCT_FAMILY_ID
                                    }))
                                  }}
                                  className='asset-registration-modal-select'
                                  renderValue={(selected) => {
                                    if (selected === '') {
                                      return <span className='asset-registration-select-placeholder'>Select Product / Asset Name</span>
                                    }

                                    return <span className='asset-registration-select-value'>{selected}</span>
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      className: 'asset-registration-modal-select-menu'
                                    }
                                  }}
                                >
                                  {ASSET_NAME_OPTIONS.map((option) => (
                                    <MenuItem className='asset-registration-modal-option' key={option} value={option}>{option}</MenuItem>
                                  ))}
                                </Select>
                                )
                              : (
                                <TextField
                                  id='asset-details-name'
                                  size='small'
                                  fullWidth
                                  value={assetDetailsForm.name}
                                  disabled={assetDetailsMode === 'view'}
                                  onChange={(event) => {
                                    setAssetDetailsForm((previousState) => ({
                                      ...previousState,
                                      name: event.target.value
                                    }))
                                  }}
                                />
                                )}
                          </div>

                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-product-family'>Product Family</label>
                            {isAssetDetailsProductFamilyDifferent
                              ? (
                                <TextField
                                  id='asset-details-product-family'
                                  size='small'
                                  fullWidth
                                  value={assetDetailsProductFamilyLabel}
                                  disabled
                                />
                                )
                              : (
                                <Select
                                  id='asset-details-product-family'
                                  size='small'
                                  displayEmpty
                                  fullWidth
                                  value={assetDetailsForm.assetTypeId}
                                  disabled={assetDetailsMode === 'view' || assetDetailsForm.name.trim() !== ''}
                                  onChange={(event) => {
                                    setAssetDetailsForm((previousState) => ({
                                      ...previousState,
                                      assetTypeId: event.target.value,
                                      name: event.target.value === FIXED_PRODUCT_FAMILY_ID ? resolveAssetNameFromInput(previousState.name) : previousState.name
                                    }))
                                  }}
                                  className='asset-registration-modal-select'
                                  renderValue={(selected) => {
                                    if (selected === '') {
                                      return <span className='asset-registration-select-placeholder'>Select Product Family</span>
                                    }

                                    const selectedLabel = PRODUCT_FAMILY_OPTIONS.find((option) => option.value === selected)?.label ?? 'Select Product Family'

                                    return <span className='asset-registration-select-value'>{selectedLabel}</span>
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      className: 'asset-registration-modal-select-menu'
                                    }
                                  }}
                                >
                                  {PRODUCT_FAMILY_OPTIONS.map((option) => (
                                    <MenuItem className='asset-registration-modal-option' key={option.value} value={option.value}>{option.label}</MenuItem>
                                  ))}
                                </Select>
                                )}
                          </div>

                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-account-name'>Account Name</label>
                            <TextField
                              id='asset-details-account-name'
                              size='small'
                              fullWidth
                              value={assetDetailsForm.applicationType}
                              disabled={assetDetailsMode === 'view'}
                              onChange={(event) => {
                                setAssetDetailsForm((previousState) => ({
                                  ...previousState,
                                  applicationType: event.target.value
                                }))
                              }}
                            />
                          </div>

                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-location'>Asset Location</label>
                            <TextField
                              id='asset-details-location'
                              size='small'
                              fullWidth
                              value={assetDetailsForm.timezone}
                              disabled={assetDetailsMode === 'view'}
                              onChange={(event) => {
                                setAssetDetailsForm((previousState) => ({
                                  ...previousState,
                                  timezone: event.target.value
                                }))
                              }}
                            />
                          </div>

                          <div className='asset-registration-review-field'>
                            <label htmlFor='asset-details-description'>Asset Description</label>
                            <TextField
                              id='asset-details-description'
                              size='small'
                              fullWidth
                              value={assetDetailsForm.description}
                              disabled={assetDetailsMode === 'view'}
                              onChange={(event) => {
                                setAssetDetailsForm((previousState) => ({
                                  ...previousState,
                                  description: event.target.value
                                }))
                              }}
                            />
                          </div>

                          <div className='asset-registration-review-field asset-registration-review-date'>
                            <label htmlFor='asset-details-recorded-date'>Recorded Date</label>
                            <TextField
                              id='asset-details-recorded-date'
                              size='small'
                              fullWidth
                              type='date'
                              value={assetDetailsForm.installDate}
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>

                        <div className='asset-registration-actions asset-registration-actions-review'>
                          <div className='asset-registration-actions-left'>
                            {assetDetailsMode === 'update' && (
                              <span
                                className='asset-registration-upload-indicator'
                                aria-label='Upload Image available soon'
                                title='Upload Image (Yet to be implemented)'
                              >
                                <UploadFileOutlinedIcon fontSize='small' />
                                <span>Upload Image</span>
                              </span>
                            )}
                            <Button
                              variant='outlined'
                              className='asset-registration-review-cancel asset-registration-back-button'
                              onClick={() => {
                                handleCloseAssetDetailsView()
                                setIsRegisteredAssetsView(true)
                              }}
                            >
                              Back
                            </Button>
                          </div>
                          <div className='asset-registration-actions-right'>
                            {assetDetailsMode === 'update' && (
                              <Button className='asset-registration-continue-button' variant='contained' disableElevation disabled={isUpdateDetailsDisabled} onClick={handleUpdateAssetDetails}>
                                {isUpdatingAssetDetails
                                  ? (
                                    <span className='asset-registration-submit-loading'>
                                      <CircularProgress size={16} color='inherit' />
                                      Updating...
                                    </span>
                                    )
                                  : 'Update'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )
              : isRegisteredAssetsView
                ? (
                  <>
                    <div className='asset-registration-registered-toolbar'>
                      <div className='asset-registration-registered-search'>
                        <TextField
                          id='registered-assets-search'
                          size='small'
                          fullWidth
                          placeholder='Search'
                          value={registeredAssetsSearchInput}
                          onChange={(event) => { setRegisteredAssetsSearchInput(event.target.value) }}
                          onKeyDown={handleRegisteredAssetsSearchKeyDown}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                aria-label='Search registered assets'
                                size='small'
                                onClick={handleRegisteredAssetsSearchApply}
                              >
                                <SearchIcon fontSize='small' />
                              </IconButton>
                            )
                          }}
                        />
                      </div>
                      <Button className='asset-registration-review-cancel asset-registration-bulk-register-button' variant='outlined' disableElevation onClick={handleOpenBulkRegisterModal}>
                        Bulk Register
                      </Button>
                      <Button className='asset-registration-continue-button' variant='contained' disableElevation onClick={handleRegisterAssetClick}>
                        Register Asset
                      </Button>
                    </div>
                    <div className='asset-registration-table-shell'>
                      <table className='asset-registration-table'>
                        <colgroup>
                          <col className='asset-registration-col-sticky-first' />
                          <col />
                          <col />
                          <col />
                          <col />
                          <col className='asset-registration-col-sticky-last' />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Product/ Asset Name</th>
                            <th>Account Name</th>
                            <th>Site Location Name</th>
                            <th>Product Family</th>
                            <th>
                              <button
                                type='button'
                                className='asset-registration-sort-button'
                                onClick={handleRecordedDateSortToggle}
                                aria-label={`Sort Recorded Date ${registeredAssetsSortDirection === 'desc' ? 'ascending' : 'descending'}`}
                              >
                                <span>Recorded Date</span>
                                <span className='asset-registration-sort-indicator'>
                                  {registeredAssetsSortDirection === 'desc' ? '↓' : '↑'}
                                </span>
                              </button>
                            </th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isAssetsLoading
                            ? (
                              <tr>
                                <td colSpan={6} className='asset-registration-table-placeholder'>
                                  <CircularProgress size={24} />
                                </td>
                              </tr>
                              )
                            : registeredAssetRows.length === 0
                              ? (
                                <tr>
                                  <td colSpan={6} className='asset-registration-table-placeholder'>
                                    No registered assets found.
                                  </td>
                                </tr>
                                )
                              : registeredAssetRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>
                                      <button type='button' className='asset-registration-table-link' onClick={() => { handleOpenAssetDetailsView(row.id, 'view') }}>
                                        {row.productAssetName}
                                      </button>
                                    </td>
                                    <td>{row.accountName}</td>
                                    <td>{row.siteLocationName}</td>
                                    <td>{row.productFamily}</td>
                                    <td>{row.recordedDate}</td>
                                    <td>
                                      <div className='asset-registration-table-action-group'>
                                        <button
                                          type='button'
                                          className='asset-registration-table-action-icon'
                                          onClick={() => { handleOpenAssetDetailsView(row.id, 'view') }}
                                          aria-label='View asset details'
                                          title='View'
                                        >
                                          <VisibilityOutlinedIcon fontSize='small' />
                                        </button>
                                        <button
                                          type='button'
                                          className='asset-registration-table-action-icon'
                                          onClick={() => { handleOpenAssetDetailsView(row.id, 'update') }}
                                          aria-label='Edit asset details'
                                          title='Edit'
                                        >
                                          <EditOutlinedIcon fontSize='small' />
                                        </button>
                                        <button
                                          type='button'
                                          className='asset-registration-table-action-icon'
                                          aria-label='Upload attachment'
                                          title='Upload Attachment'
                                        >
                                          <UploadFileOutlinedIcon fontSize='small' />
                                        </button>
                                        <button
                                          type='button'
                                          className='asset-registration-table-action-icon'
                                          onClick={handleOpenAssetDocumentation}
                                          aria-label='Open Documentation'
                                          title='Documentation'
                                        >
                                          <img src={docuthekLogo} alt='Documentation' className='asset-registration-doc-icon' />
                                        </button>
                                        <button
                                          type='button'
                                          className='asset-registration-table-action-icon'
                                          onClick={handleOpenAdlatusPortal}
                                          aria-label='Open Adlatus portal'
                                          title='Adlatus'
                                        >
                                          <img src={adlatusLogo} alt='Adlatus' className='asset-registration-adlatus-icon' />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                              ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      count={registeredAssetsCount}
                      page={registeredAssetsPage}
                      setPage={setRegisteredAssetsPage}
                      rowsPerPage={registeredAssetsRowsPerPage}
                    />
                  </>
                  )
                : !isReviewStep
                    ? (
                    <>
                      <div className='asset-registration-field-group'>
                        <label htmlFor='asset-serial-number'>
                          Serial Number <span>Required</span>
                        </label>
                        <TextField
                          id='asset-serial-number'
                          size='small'
                          fullWidth
                          placeholder='Enter Serial Number'
                          value={serialNumber}
                          onChange={(event) => {
                            setSerialNumber(event.target.value)
                            if (!serialNumberTouched) {
                              setSerialNumberTouched(true)
                            }
                          }}
                          onBlur={() => { setSerialNumberTouched(true) }}
                          error={serialNumberTouched && !isSerialNumberValid}
                          helperText={serialNumberTouched && !isSerialNumberValid ? getSerialNumberErrorMessage() : ' '}
                        />
                        <div className='asset-registration-serial-actions'>
                          <button type='button' className='asset-registration-inline-action asset-registration-qr-action' onClick={handleOpenQrScanner} disabled={!canUseQrScanner}>
                            <QrCodeScannerIcon fontSize='small' />
                            Scan QR Code
                          </button>
                        </div>
                      </div>

                      <div className='asset-registration-actions asset-registration-actions-initial'>
                        <div className='asset-registration-actions-left'>
                          <Button variant='outlined' className='asset-registration-review-cancel asset-registration-back-button' onClick={handleRegisteredAssetsClick}>
                            Cancel
                          </Button>
                        </div>
                        <div className='asset-registration-actions-right'>
                          <Button className='asset-registration-continue-button' variant='contained' disableElevation disabled={!isSerialNumberValid} onClick={handleContinueClick}>
                            Continue
                          </Button>
                        </div>
                      </div>
                    </>
                      )
                    : (
                    <>
                      <div className='asset-registration-review-grid'>
                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-serial-review'>Serial Number</label>
                          <div className='asset-registration-input-action-row'>
                            <div className='asset-registration-row-input'>
                              <TextField
                                id='asset-serial-review'
                                size='small'
                                fullWidth
                                value={trimmedSerialNumber}
                                disabled
                              />
                            </div>
                            <button type='button' className='asset-registration-inline-action' onClick={handleChangeAsset}>Change Asset</button>
                          </div>
                        </div>

                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-name'>Product / Asset Name</label>
                          {selectedProductFamily === FIXED_PRODUCT_FAMILY_ID && !useScannedAssetNameFreeText
                            ? (
                              <Select
                                id='asset-name'
                                size='small'
                                displayEmpty
                                fullWidth
                                value={assetName}
                                onChange={(event) => {
                                  setAssetName(event.target.value)
                                  setSelectedProductFamily(FIXED_PRODUCT_FAMILY_ID)
                                  setUseScannedAssetNameFreeText(false)
                                }}
                                className='asset-registration-modal-select'
                                renderValue={(selected) => {
                                  if (selected === '') {
                                    return <span className='asset-registration-select-placeholder'>Select Product / Asset Name</span>
                                  }

                                  return <span className='asset-registration-select-value'>{selected}</span>
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    className: 'asset-registration-modal-select-menu'
                                  }
                                }}
                              >
                                {ASSET_NAME_OPTIONS.map((option) => (
                                  <MenuItem className='asset-registration-modal-option' key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                              )
                            : (
                              <TextField
                                id='asset-name'
                                size='small'
                                fullWidth
                                placeholder='Enter Product / Asset Name'
                                value={assetName}
                                onChange={(event) => {
                                  setAssetName(event.target.value)
                                  if (selectedProductFamily !== FIXED_PRODUCT_FAMILY_ID) {
                                    setUseScannedAssetNameFreeText(false)
                                  }
                                }}
                              />
                              )}
                        </div>

                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-product-family'>Product Family</label>
                          {isSelectedProductFamilyDifferent
                            ? (
                              <TextField
                                id='asset-product-family'
                                size='small'
                                fullWidth
                                value={selectedProductFamilyLabel}
                                disabled
                              />
                              )
                            : (
                              <Select
                                id='asset-product-family'
                                size='small'
                                displayEmpty
                                fullWidth
                                value={selectedProductFamily}
                                disabled={assetName.trim() !== ''}
                                onChange={(event) => {
                                  const nextProductFamily = event.target.value

                                  setSelectedProductFamily(nextProductFamily)
                                  if (nextProductFamily === FIXED_PRODUCT_FAMILY_ID) {
                                    if (!useScannedAssetNameFreeText) {
                                      setAssetName((previousAssetName) => resolveAssetNameFromInput(previousAssetName))
                                    }
                                  } else {
                                    setUseScannedAssetNameFreeText(false)
                                  }
                                }}
                                className='asset-registration-modal-select'
                                renderValue={(selected) => {
                                  if (selected === '') {
                                    return <span className='asset-registration-select-placeholder'>Select Product Family</span>
                                  }

                                  const selectedLabel = PRODUCT_FAMILY_OPTIONS.find((option) => option.value === selected)?.label ?? 'Select Product Family'

                                  return <span className='asset-registration-select-value'>{selectedLabel}</span>
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    className: 'asset-registration-modal-select-menu'
                                  }
                                }}
                              >
                                {PRODUCT_FAMILY_OPTIONS.map((option) => (
                                  <MenuItem className='asset-registration-modal-option' key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                              </Select>
                              )}
                        </div>

                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-account-name'>Account Name</label>
                          <TextField
                            id='asset-account-name'
                            size='small'
                            fullWidth
                            placeholder='Enter Customer/Enterprise Name'
                            value={accountName}
                            onChange={(event) => { setAccountName(event.target.value) }}
                          />
                        </div>

                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-location'>Asset Location</label>
                          <div className='asset-registration-input-action-row'>
                            <div className='asset-registration-row-input'>
                              <TextField
                                id='asset-location'
                                size='small'
                                fullWidth
                                placeholder='Enter Location'
                                value={assetLocation}
                                onChange={(event) => { setAssetLocation(event.target.value) }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className='asset-registration-review-field'>
                          <label htmlFor='asset-description'>Asset Description</label>
                          <TextField
                            id='asset-description'
                            size='small'
                            fullWidth
                            placeholder='Description'
                            value={assetDescription}
                            onChange={(event) => { setAssetDescription(event.target.value) }}
                          />
                        </div>

                        <div className='asset-registration-review-field asset-registration-review-date'>
                          <label htmlFor='asset-recorded-date'>Recorded Date</label>
                          <TextField
                            id='asset-recorded-date'
                            size='small'
                            fullWidth
                            type='date'
                            value={recordedDate}
                            disabled
                            InputLabelProps={{ shrink: true }}
                          />
                        </div>
                      </div>

                      <div className='asset-registration-actions asset-registration-actions-review'>
                        <div className='asset-registration-actions-left'>
                          <span
                            className='asset-registration-upload-indicator'
                            aria-label='Upload Image available soon'
                            title='Upload Image (Yet to be implemented)'
                          >
                            <UploadFileOutlinedIcon fontSize='small' />
                            <span>Upload Image</span>
                          </span>
                          <Button variant='outlined' className='asset-registration-review-cancel' onClick={handleCancelReview}>
                            Back
                          </Button>
                        </div>
                        <div className='asset-registration-actions-right'>
                          <Button className='asset-registration-continue-button' variant='contained' disableElevation disabled={isSubmitDisabled} onClick={handleSubmitAsset}>
                            {isSubmitting
                              ? (
                                <span className='asset-registration-submit-loading'>
                                  <CircularProgress size={16} color='inherit' />
                                  Submitting...
                                </span>
                                )
                              : 'Submit'}
                          </Button>
                        </div>
                      </div>
                    </>
                      )}
          </section>

        </div>
      </div>

      <Dialog
        open={isQrScannerOpen}
        onClose={handleCloseQrScanner}
        TransitionProps={{
          onEntered: () => { setIsQrScannerDialogReady(true) }
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{ className: 'asset-registration-modal-paper' }}
      >
        <DialogContent className='asset-registration-modal-content'>
          <div className='asset-registration-modal-header'>
            <h3>Scan Serial Number QR Code</h3>
            <IconButton onClick={handleCloseQrScanner} className='asset-registration-modal-close'>
              <CloseIcon />
            </IconButton>
          </div>

          <p className='asset-registration-modal-copy'>Point your camera at the QR code. Fields will be filled automatically when a valid QR is detected.</p>

          <div className='asset-registration-qr-scanner-shell'>
            <div id={QR_SCANNER_CONTAINER_ID} className='asset-registration-qr-video' />
            {isQrScannerLoading && <div className='asset-registration-qr-overlay'>Starting camera...</div>}
          </div>

          {qrScannerError !== null && <div className='asset-registration-inline-message'>{qrScannerError}</div>}

          <div className='asset-registration-modal-actions'>
            <Button variant='outlined' className='asset-registration-modal-cancel' onClick={handleCloseQrScanner}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isBulkRegisterModalOpen}
        onClose={handleCloseBulkRegisterModal}
        maxWidth='sm'
        fullWidth
        PaperProps={{ className: 'asset-registration-modal-paper' }}
      >
        <DialogContent className='asset-registration-modal-content'>
          <div className='asset-registration-modal-header'>
            <h3>Bulk Register Assets</h3>
            <IconButton onClick={handleCloseBulkRegisterModal} className='asset-registration-modal-close' disabled={isBulkRegisterSubmitting}>
              <CloseIcon />
            </IconButton>
          </div>

          <p className='asset-registration-modal-copy'>Download the template, fill all details, and then upload the completed file.</p>

          <a
            href={assetRegistrationTemplate}
            download='AssetRegistration.xlsx'
            className='asset-registration-header-inline-link asset-registration-bulk-template-link'
          >
            Download Template
          </a>

          <div className='asset-registration-bulk-upload-input-wrap'>
            <input
              id='asset-registration-bulk-file'
              type='file'
              accept='.xlsx,.xls'
              onChange={handleBulkRegisterFileChange}
              disabled={isBulkRegisterSubmitting}
            />
          </div>

          {bulkRegisterError !== null && <div className='asset-registration-inline-message'>{bulkRegisterError}</div>}

          {bulkRegisterResult !== null && (
            <div className='asset-registration-bulk-summary'>
              <h4>Upload Summary</h4>
              <p>{`Total Rows: ${bulkRegisterResult.total_rows}`}</p>
              <p>{`Success Count: ${bulkRegisterResult.success_count}`}</p>
              <p>{`Failure Count: ${bulkRegisterResult.failure_count}`}</p>
              {bulkRegisterResult.failure_reasons.length > 0 && (
                <div className='asset-registration-bulk-failures'>
                  <h5>Failed Rows</h5>
                  <ul>
                    {bulkRegisterResult.failure_reasons.map((failureReason) => (
                      <li key={`${failureReason.row_number}-${failureReason.reason}`}>
                        {`Row ${failureReason.row_number}: ${failureReason.reason}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className='asset-registration-modal-actions'>
            <Button variant='outlined' className='asset-registration-modal-cancel' onClick={handleCloseBulkRegisterModal} disabled={isBulkRegisterSubmitting}>
              Cancel
            </Button>
            <Button variant='contained' className='asset-registration-continue-button' onClick={handleBulkRegisterSubmit} disabled={isBulkRegisterDisabled}>
              {isBulkRegisterSubmitting
                ? (
                  <span className='asset-registration-submit-loading'>
                    <CircularProgress size={16} color='inherit' />
                    Registering...
                  </span>
                  )
                : 'Register'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AssetRegistration
